var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var _a;
const isDataSavingMode = (_a = navigator == null ? void 0 : navigator.connection) == null ? void 0 : _a.saveData;
function convertSectionQueryParamsToURLSearchParams(queryParams) {
  if (queryParams instanceof URLSearchParams) return queryParams;
  else {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(queryParams)) {
      if (value === null) continue;
      params.set(key, value.toString());
    }
    return params;
  }
}
class SectionLoader {
  constructor() {
    __publicField(this, "pendingRequests", /* @__PURE__ */ new Map());
    __publicField(this, "prefetchRequests", /* @__PURE__ */ new Set());
    __publicField(this, "prefetchCache", /* @__PURE__ */ new Map());
    __publicField(this, "ongoingFetches", /* @__PURE__ */ new Set());
    __publicField(this, "fetchSectionsTimeout", null);
    __publicField(this, "prefetchSectionsTimeout", null);
    __publicField(this, "cacheMaxSize", 100);
    __publicField(this, "maxSectionsPerRequest", 4);
  }
  buildCacheKey(sectionId, path, queryParams) {
    return JSON.stringify({
      path: path || window.location.pathname,
      sectionId,
      queryParams: queryParams ? convertSectionQueryParamsToURLSearchParams(queryParams).toString() : ""
    });
  }
  buildRequestGroups(sectionKeys) {
    const groups = sectionKeys.reduce(
      (acc, key) => {
        const { path, sectionId, queryParams } = JSON.parse(key);
        const groupKey = JSON.stringify({
          path,
          queryParams
        });
        const params = queryParams ? new URLSearchParams(queryParams) : void 0;
        if (!acc[groupKey]) {
          acc[groupKey] = {
            path,
            queryParams: params,
            keys: [],
            sectionIds: []
          };
        }
        acc[groupKey].keys.push(key);
        acc[groupKey].sectionIds.push(sectionId);
        return acc;
      },
      {}
    );
    return Object.values(groups);
  }
  chunk(arr, size) {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  }
  scheduleFetchSections() {
    if (this.fetchSectionsTimeout) {
      clearTimeout(this.fetchSectionsTimeout);
    }
    this.fetchSectionsTimeout = setTimeout(() => this.fetchSections(), 50);
  }
  schedulePrefetchSections() {
    if (this.prefetchSectionsTimeout) {
      clearTimeout(this.prefetchSectionsTimeout);
    }
    this.prefetchSectionsTimeout = setTimeout(
      () => this.prefetchSections(),
      50
    );
  }
  async fetchSections() {
    if (this.pendingRequests.size === 0) {
      return;
    }
    const sectionKeys = Array.from(this.pendingRequests.keys());
    const requestGroups = this.buildRequestGroups(sectionKeys);
    await Promise.allSettled(
      requestGroups.flatMap(({ path, queryParams, keys, sectionIds }) => {
        const chunks = this.chunk(sectionIds, this.maxSectionsPerRequest);
        return chunks.map(async (chunk, chunkIndex) => {
          const chunkKeys = chunk.map(
            (sectionId) => keys[sectionIds.indexOf(sectionId)]
          );
          try {
            const url = new URL(window.location.href);
            url.pathname = path || window.location.pathname;
            if (queryParams) {
              url.search = queryParams.toString();
            }
            url.searchParams.set("sections", chunk.join(","));
            const requestUrl = url.toString();
            const response = await fetch(requestUrl);
            if (!response.ok) throw new Error(`Failed to fetch: ${requestUrl}`);
            const sectionsData = await response.json();
            chunkKeys.forEach((key, index) => {
              const callbacks = this.pendingRequests.get(key) || [];
              this.pendingRequests.delete(key);
              const sectionId = chunk[index];
              const data = sectionsData[sectionId];
              if (data === void 0) {
                callbacks.forEach(
                  ({ reject }) => reject(new Error(`No data for section ${sectionId}`))
                );
              } else {
                callbacks.forEach(({ resolve }) => resolve(data));
                this.addToCache(key, data);
              }
              this.ongoingFetches.delete(key);
            });
          } catch (error) {
            console.error(error);
            chunkKeys.forEach((key) => {
              const callbacks = this.pendingRequests.get(key) || [];
              this.pendingRequests.delete(key);
              callbacks.forEach(({ reject }) => reject(error));
              this.ongoingFetches.delete(key);
            });
          }
        });
      })
    );
  }
  async prefetchSections() {
    if (this.prefetchRequests.size === 0) {
      return;
    }
    const sectionKeys = Array.from(this.prefetchRequests);
    const requestGroups = this.buildRequestGroups(sectionKeys);
    await Promise.allSettled(
      requestGroups.map(async ({ path, queryParams, keys, sectionIds }) => {
        try {
          const url = new URL(window.location.href);
          url.pathname = path || window.location.pathname;
          if (queryParams) {
            url.search = queryParams.toString();
          }
          url.searchParams.set("sections", sectionIds.join(","));
          const requestUrl = url.toString();
          const response = await fetch(requestUrl, {
            priority: "low"
          });
          if (!response.ok) {
            throw new Error(`Failed to prefetch: ${requestUrl}`);
          }
          const sectionsData = await response.json();
          keys.forEach((key, index) => {
            const sectionId = sectionIds[index];
            const data = sectionsData[sectionId];
            if (data !== void 0) {
              this.addToCache(key, data);
            }
            this.prefetchRequests.delete(key);
            this.ongoingFetches.delete(key);
            const callbacks = this.pendingRequests.get(key) || [];
            this.pendingRequests.delete(key);
            callbacks.forEach(({ resolve, reject }) => {
              if (data !== void 0) {
                resolve(data);
              } else {
                reject(new Error(`No data for section ${sectionId}`));
              }
            });
          });
        } catch (error) {
          console.error(error);
          keys.forEach((key) => {
            this.prefetchRequests.delete(key);
            this.ongoingFetches.delete(key);
            const callbacks = this.pendingRequests.get(key) || [];
            this.pendingRequests.delete(key);
            callbacks.forEach(({ reject }) => reject(error));
          });
        }
      })
    );
  }
  addToCache(key, data) {
    if (this.prefetchCache.size > this.cacheMaxSize) {
      this.prefetchCache.delete(this.prefetchCache.keys().next().value);
    }
    this.prefetchCache.set(key, data);
  }
  loadSection(sectionId, path = "", queryParams) {
    const key = this.buildCacheKey(sectionId, path, queryParams);
    if (this.prefetchCache.has(key)) {
      return Promise.resolve(this.prefetchCache.get(key));
    }
    return new Promise((resolve, reject) => {
      if (!this.pendingRequests.has(key)) {
        this.pendingRequests.set(key, []);
      }
      this.pendingRequests.get(key).push({ resolve, reject });
      if (!this.ongoingFetches.has(key) && !this.prefetchRequests.has(key)) {
        this.ongoingFetches.add(key);
        this.scheduleFetchSections();
      }
    });
  }
  prefetchSection(sectionId, path = "", queryParams) {
    if (isDataSavingMode) {
      return;
    }
    const key = this.buildCacheKey(sectionId, path, queryParams);
    if (this.pendingRequests.has(key) || this.ongoingFetches.has(key) || this.prefetchCache.has(key) || this.prefetchRequests.has(key)) {
      return;
    }
    this.prefetchRequests.add(key);
    this.ongoingFetches.add(key);
    this.schedulePrefetchSections();
  }
  cancelPrefetchSection(sectionId, path = "", queryParams) {
    this.prefetchRequests.delete(
      this.buildCacheKey(sectionId, path, queryParams)
    );
    if (this.prefetchRequests.size) {
      this.schedulePrefetchSections();
    }
  }
}
const sectionLoader = new SectionLoader();
const loadSection = sectionLoader.loadSection.bind(sectionLoader);
const prefetchSection = sectionLoader.prefetchSection.bind(sectionLoader);
sectionLoader.cancelPrefetchSection.bind(sectionLoader);
var commonjsGlobal = typeof globalThis !== "undefined" ? globalThis : typeof window !== "undefined" ? window : typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : {};
function getDefaultExportFromCjs(x) {
  return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, "default") ? x["default"] : x;
}
var FUNC_ERROR_TEXT = "Expected a function";
var NAN = 0 / 0;
var symbolTag = "[object Symbol]";
var reTrim = /^\s+|\s+$/g;
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;
var reIsBinary = /^0b[01]+$/i;
var reIsOctal = /^0o[0-7]+$/i;
var freeParseInt = parseInt;
var freeGlobal = typeof commonjsGlobal == "object" && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;
var freeSelf = typeof self == "object" && self && self.Object === Object && self;
var root = freeGlobal || freeSelf || Function("return this")();
var objectProto = Object.prototype;
var objectToString = objectProto.toString;
var nativeMax = Math.max, nativeMin = Math.min;
var now = function() {
  return root.Date.now();
};
function debounce(func, wait, options) {
  var lastArgs, lastThis, maxWait, result, timerId, lastCallTime, lastInvokeTime = 0, leading = false, maxing = false, trailing = true;
  if (typeof func != "function") {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  wait = toNumber(wait) || 0;
  if (isObject(options)) {
    leading = !!options.leading;
    maxing = "maxWait" in options;
    maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
    trailing = "trailing" in options ? !!options.trailing : trailing;
  }
  function invokeFunc(time) {
    var args = lastArgs, thisArg = lastThis;
    lastArgs = lastThis = void 0;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }
  function leadingEdge(time) {
    lastInvokeTime = time;
    timerId = setTimeout(timerExpired, wait);
    return leading ? invokeFunc(time) : result;
  }
  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime, timeSinceLastInvoke = time - lastInvokeTime, result2 = wait - timeSinceLastCall;
    return maxing ? nativeMin(result2, maxWait - timeSinceLastInvoke) : result2;
  }
  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime, timeSinceLastInvoke = time - lastInvokeTime;
    return lastCallTime === void 0 || timeSinceLastCall >= wait || timeSinceLastCall < 0 || maxing && timeSinceLastInvoke >= maxWait;
  }
  function timerExpired() {
    var time = now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    timerId = setTimeout(timerExpired, remainingWait(time));
  }
  function trailingEdge(time) {
    timerId = void 0;
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = void 0;
    return result;
  }
  function cancel() {
    if (timerId !== void 0) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = void 0;
  }
  function flush() {
    return timerId === void 0 ? result : trailingEdge(now());
  }
  function debounced() {
    var time = now(), isInvoking = shouldInvoke(time);
    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;
    if (isInvoking) {
      if (timerId === void 0) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === void 0) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}
function isObject(value) {
  var type = typeof value;
  return !!value && (type == "object" || type == "function");
}
function isObjectLike(value) {
  return !!value && typeof value == "object";
}
function isSymbol(value) {
  return typeof value == "symbol" || isObjectLike(value) && objectToString.call(value) == symbolTag;
}
function toNumber(value) {
  if (typeof value == "number") {
    return value;
  }
  if (isSymbol(value)) {
    return NAN;
  }
  if (isObject(value)) {
    var other = typeof value.valueOf == "function" ? value.valueOf() : value;
    value = isObject(other) ? other + "" : other;
  }
  if (typeof value != "string") {
    return value === 0 ? value : +value;
  }
  value = value.replace(reTrim, "");
  var isBinary = reIsBinary.test(value);
  return isBinary || reIsOctal.test(value) ? freeParseInt(value.slice(2), isBinary ? 2 : 8) : reIsBadHex.test(value) ? NAN : +value;
}
var lodash_debounce = debounce;
const debounce$1 = /* @__PURE__ */ getDefaultExportFromCjs(lodash_debounce);
const transitionQueue = [];
let isRunning = false;
const runTransitions = async () => {
  if (isRunning || transitionQueue.length === 0) return;
  isRunning = true;
  const batch = [...transitionQueue];
  transitionQueue.length = 0;
  try {
    if (document.startViewTransition) {
      document.startViewTransition(
        () => Promise.all(batch.map((callback) => callback())).then(() => {
        })
      );
    } else {
      await Promise.all(batch.map((callback) => callback()));
    }
  } catch (error) {
    console.error("Transition error:", error);
  } finally {
    isRunning = false;
  }
};
const debouncedRunTransitions = debounce$1(runTransitions, 10);
const enqueueTransition = (callback) => {
  transitionQueue.push(callback);
  debouncedRunTransitions();
};
const PRODUCT_CHANGE_EVENT = "product_change";
const PRODUCT_MAY_CHANGE_EVENT = "product_may_change";
const PRODUCT_SELECTED_VARIANT_CHANGE_EVENT = "product_selected_variant_change";
const PRODUCT_SELECTED_VARIANT_MAY_CHANGE_EVENT = "product_selected_variant_may_change";
const PRODUCT_PACK_CHANGE_EVENT = "product_pack_change";
class ProductChangeEvent extends Event {
  constructor(oldHandle, newHandle, variantId) {
    super(PRODUCT_CHANGE_EVENT);
    this.oldHandle = oldHandle;
    this.newHandle = newHandle;
    this.variantId = variantId;
  }
}
class ProductMayChangeEvent extends Event {
  constructor(currentHandle, newHandle) {
    super(PRODUCT_MAY_CHANGE_EVENT);
    this.currentHandle = currentHandle;
    this.newHandle = newHandle;
  }
}
class ProductVariantChangeEvent extends Event {
  constructor(handle, variantId) {
    super(PRODUCT_SELECTED_VARIANT_CHANGE_EVENT);
    this.handle = handle;
    this.variantId = variantId;
  }
}
class ProductVariantMayChangeEvent extends Event {
  constructor(handle, variantId) {
    super(PRODUCT_SELECTED_VARIANT_MAY_CHANGE_EVENT);
    this.handle = handle;
    this.variantId = variantId;
  }
}
class ProductPackChangeEvent extends Event {
  constructor(currentHandle, packHandle) {
    super(PRODUCT_PACK_CHANGE_EVENT);
    this.currentHandle = currentHandle;
    this.packHandle = packHandle;
  }
}
class ProductContext extends HTMLElement {
  constructor() {
    super(...arguments);
    __publicField(this, "handle", null);
    __publicField(this, "variantId", null);
    __publicField(this, "packHandle", null);
    __publicField(this, "signalHandleMayChange", debounce$1((handle) => {
      this.dispatchEvent(new ProductMayChangeEvent(this.handle, handle));
    }, 10));
    __publicField(this, "signalVariantIdMayChange", debounce$1((variantId) => {
      if (variantId !== this.variantId) {
        this.dispatchEvent(
          new ProductVariantMayChangeEvent(this.handle, variantId)
        );
      }
    }, 10));
    __publicField(this, "onExternalProductChanged", (event) => {
      var _a2, _b;
      const customEvent = event;
      const handle = (_a2 = customEvent.detail) == null ? void 0 : _a2.productHandle;
      const variantId = (_b = customEvent.detail) == null ? void 0 : _b.variant;
      if (handle) {
        this.setHandle(handle);
      }
      if (variantId) {
        this.setVariantId(variantId);
      }
    });
    __publicField(this, "onExternalPackChanged", (event) => {
      var _a2;
      const customEvent = event;
      const packHandle = (_a2 = customEvent.detail) == null ? void 0 : _a2.packHandle;
      if (packHandle) {
        this.setPackHandle(packHandle);
      }
    });
  }
  connectedCallback() {
    this.handle = this.getAttribute("handle");
    this.variantId = this.getAttribute("variant-id");
    this.packHandle = null;
    this.checkUrlForUpdates();
    this.addEventListener("product:changed", this.onExternalProductChanged);
    this.addEventListener("product:pack-changed", this.onExternalPackChanged);
  }
  setHandle(handle) {
    if (handle !== this.handle) {
      const oldHandle = this.handle;
      this.handle = handle;
      this.variantId = null;
      this.packHandle = null;
      this.setAttribute("handle", this.handle);
      this.removeAttribute("variant-id");
      const url = new URL(
        `${window.location.origin}/products/${handle}${window.location.search}`
      );
      url.searchParams.delete("variant");
      window.history.replaceState(null, "", url.toString());
      if (oldHandle) {
        this.dispatchEvent(
          new ProductChangeEvent(oldHandle, handle, this.variantId)
        );
      }
    }
  }
  setVariantId(variantId) {
    if (variantId !== this.variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variantId);
      window.history.replaceState(null, "", url.toString());
      this.setAttribute("variant-id", variantId);
      this.variantId = variantId;
      this.dispatchEvent(
        new ProductVariantChangeEvent(this.handle, variantId)
      );
    }
  }
  setPackHandle(packHandle) {
    if (packHandle !== this.packHandle) {
      this.packHandle = packHandle;
      this.setAttribute("pack-handle", packHandle);
      this.setAttribute("selling-plan", "");
      this.dispatchEvent(new ProductPackChangeEvent(this.handle, packHandle));
    }
  }
  listen(event, cb) {
    this.addEventListener(event, cb);
    return () => {
      this.removeEventListener(event, cb);
    };
  }
  checkUrlForUpdates() {
    const { pathname } = window.location;
    const handle = pathname.split("/").pop();
    if (handle) {
      this.setHandle(handle);
    }
  }
}
function defineProductContextChild(tag, updateOnProductChange = true, updateOnPackChange = false) {
  class ProductContextChild extends HTMLElement {
    constructor() {
      super();
      __publicField(this, "sectionId");
      __publicField(this, "productContext");
      __publicField(this, "unbindProductChange");
      __publicField(this, "unbindProductMayChange");
      __publicField(this, "unbindProductPackChange");
      __publicField(this, "handleProductChange", (ev) => {
        if (ev instanceof ProductChangeEvent) {
          this.loadSection(ev.newHandle, ev.variantId);
        }
      });
      __publicField(this, "handleProductPackChange", (ev) => {
        if (ev instanceof ProductPackChangeEvent) {
          this.loadSection(ev.packHandle);
        }
      });
      __publicField(this, "handleProductMayChange", (ev) => {
        if (ev instanceof ProductMayChangeEvent) {
          return prefetchSection(this.sectionId, `/products/${ev.newHandle}`);
        }
      });
      if (this.hasAttribute("section-id")) {
        this.sectionId = this.getAttribute("section-id");
      } else {
        throw new Error(
          `\`section-id\` must be specified with <${this.tagName.toLowerCase()}> or it will not refresh when color is changed.`
        );
      }
      if (this.isConnected) {
        this.bindProductContext();
      }
    }
    connectedCallback() {
      if (!this.productContext) {
        this.bindProductContext();
      }
    }
    disconnectedCallback() {
      var _a2, _b, _c;
      (_a2 = this.unbindProductChange) == null ? void 0 : _a2.call(this);
      (_b = this.unbindProductMayChange) == null ? void 0 : _b.call(this);
      (_c = this.unbindProductPackChange) == null ? void 0 : _c.call(this);
      this.unbindProductChange = void 0;
      this.unbindProductMayChange = void 0;
      this.unbindProductPackChange = void 0;
    }
    bindProductContext() {
      const productContext = this.closest("product-context");
      if (!productContext || typeof productContext.listen !== "function") {
        throw new Error(
          `<${this.tagName.toLowerCase()}> must be a child of <product-context>`
        );
      }
      this.productContext = productContext;
      if (updateOnProductChange) {
        this.unbindProductChange = this.productContext.listen(
          PRODUCT_CHANGE_EVENT,
          this.handleProductChange
        );
        this.unbindProductMayChange = this.productContext.listen(
          PRODUCT_MAY_CHANGE_EVENT,
          this.handleProductMayChange
        );
      }
      if (updateOnPackChange) {
        this.unbindProductPackChange = this.productContext.listen(
          PRODUCT_PACK_CHANGE_EVENT,
          this.handleProductPackChange
        );
      }
    }
    async loadSection(handle, variantId = null) {
      const content = await loadSection(
        this.sectionId,
        `/products/${handle}`,
        variantId ? { variant: variantId } : void 0
      );
      if (!content) return;
      const range = document.createRange();
      const documentFragment = range.createContextualFragment(content);
      const matchingWc = documentFragment.querySelector(this.tagName);
      if (!matchingWc) return;
      this.replaceSection(matchingWc);
      return matchingWc;
    }
    replaceSection(newComponent) {
      this.beforeSectionMount(this, newComponent);
      enqueueTransition(async () => {
        this.replaceWith(newComponent);
        newComponent.afterSectionMount(newComponent);
      });
    }
    beforeSectionMount(oldComponent, newComponent) {
    }
    afterSectionMount(newComponent) {
    }
  }
  if (!customElements.get(tag)) {
    customElements.define(tag, ProductContextChild);
  }
}
if (!customElements.get("product-context")) {
  customElements.define("product-context", ProductContext);
}
defineProductContextChild("product-heading", true, true);
defineProductContextChild("media-gallery", true, true);
defineProductContextChild("buy-buttons", true, true);
defineProductContextChild("product-description", true, false);
defineProductContextChild("subscription-form", true, true);
defineProductContextChild("product-blocks", true, false);
defineProductContextChild("product-section", true, false);
defineProductContextChild("product-sticky-atc-inner", true, true);
class ProductPicker extends HTMLElement {
  constructor() {
    super(...arguments);
    __publicField(this, "productContext");
    __publicField(this, "selectEl");
    __publicField(this, "nameEl");
    __publicField(this, "imageEl");
    __publicField(this, "handleChange", (event) => {
      var _a2;
      if (!this.selectEl || !this.productContext) return;
      const selectedOption = this.selectEl.options[this.selectEl.selectedIndex];
      if (this.nameEl && selectedOption) {
        this.nameEl.textContent = ((_a2 = selectedOption.textContent) == null ? void 0 : _a2.trim()) || "";
      }
      const productHandle = selectedOption.getAttribute("data-handle");
      const productImage = selectedOption.getAttribute("data-image");
      if (this.imageEl && productImage) {
        this.imageEl.src = productImage;
        this.imageEl.srcset = productImage;
        this.imageEl.loading = "eager";
      }
      if (productHandle && productHandle !== this.productContext.handle) {
        this.productContext.setHandle(productHandle);
      }
    });
  }
  connectedCallback() {
    this.selectEl = this.querySelector("[data-handle-select]") || void 0;
    this.nameEl = this.querySelector("[data-name]") || void 0;
    this.imageEl = this.querySelector("img") || void 0;
    this.productContext = this.closest("product-context") || void 0;
    if (!this.selectEl || !this.productContext) return;
    this.selectEl.addEventListener("change", this.handleChange);
  }
  disconnectedCallback() {
    if (this.selectEl) {
      this.selectEl.removeEventListener("change", this.handleChange);
    }
  }
}
if (!customElements.get("product-picker")) {
  customElements.define("product-picker", ProductPicker);
}
class PackPicker extends HTMLElement {
  constructor() {
    super(...arguments);
    __publicField(this, "productContext");
    __publicField(this, "buttons");
    __publicField(this, "handleClick", (event) => {
      const target = event.currentTarget;
      const packHandle = target.getAttribute("data-pack-handle");
      if (packHandle && this.productContext) {
        this.productContext.setPackHandle(packHandle);
      }
    });
  }
  connectedCallback() {
    this.productContext = this.closest("product-context") || void 0;
    if (!this.productContext) return;
    this.buttons = this.querySelectorAll("[data-pack-handle]");
    this.buttons.forEach((btn) => btn.addEventListener("click", this.handleClick));
  }
  disconnectedCallback() {
    var _a2;
    (_a2 = this.buttons) == null ? void 0 : _a2.forEach((btn) => btn.removeEventListener("click", this.handleClick));
  }
}
if (!customElements.get("pack-picker")) {
  customElements.define("pack-picker", PackPicker);
}
class FakeVariantPicker extends HTMLElement {
  constructor() {
    super(...arguments);
    __publicField(this, "productContext");
    __publicField(this, "optionValueEls");
    __publicField(this, "selectEl");
    __publicField(this, "handleChange", (event) => {
      var _a2, _b;
      if (!((_a2 = this.optionValueEls) == null ? void 0 : _a2.length) || !this.selectEl || !this.productContext) return;
      if (event.currentTarget instanceof HTMLSelectElement) {
        this.updateDropdownOption(event.currentTarget);
      }
      const selectedOption = this.optionValueEls.filter((el) => {
        if (el instanceof HTMLInputElement && el.checked) return true;
        if (el instanceof HTMLSelectElement) return true;
      }).map((el) => el.value).join(" - ");
      const productHandle = (_b = Array.from(this.selectEl.options).find((el) => el.value == selectedOption)) == null ? void 0 : _b.getAttribute("data-handle");
      if (productHandle && productHandle !== this.productContext.handle) {
        this.productContext.setHandle(productHandle);
      }
    });
    __publicField(this, "updateDropdownOption", (el) => {
      var _a2;
      const imageUrl = el.options[el.selectedIndex].getAttribute("data-image");
      const imageEl = (_a2 = el.closest(".product-form__option-values.dropdown")) == null ? void 0 : _a2.querySelector("img");
      if (imageEl && imageUrl) {
        imageEl.style.visibility = "hidden";
        imageEl.srcset = "";
        imageEl.src = imageUrl;
        imageEl.style.visibility = "visible";
      }
    });
  }
  connectedCallback() {
    var _a2;
    this.optionValueEls = Array.from(this.querySelectorAll("[data-option-value]"));
    this.selectEl = this.querySelector("[data-product-select]");
    this.productContext = this.closest("product-context") || void 0;
    if (!((_a2 = this.optionValueEls) == null ? void 0 : _a2.length) || !this.selectEl || !this.productContext) return;
    this.optionValueEls.forEach((el) => el.addEventListener("change", this.handleChange));
  }
  disconnectedCallback() {
    var _a2;
    (_a2 = this.optionValueEls) == null ? void 0 : _a2.forEach((el) => el.removeEventListener("change", this.handleChange));
  }
}
if (!customElements.get("fake-variant-picker")) {
  customElements.define("fake-variant-picker", FakeVariantPicker);
}
class ProductStickyATC extends HTMLElement {
  constructor() {
    super(...arguments);
    __publicField(this, "productContext");
    __publicField(this, "headerSection");
    __publicField(this, "handleScroll", (event) => {
      var _a2, _b, _c, _d;
      if (!this.productContext) return;
      const bottom = ((_b = (_a2 = this.productContext) == null ? void 0 : _a2.querySelector("#AddToCart")) == null ? void 0 : _b.getBoundingClientRect().bottom) || 0;
      window.innerHeight - (((_c = this.headerSection) == null ? void 0 : _c.offsetHeight) || 0);
      if (bottom < (((_d = this.headerSection) == null ? void 0 : _d.offsetHeight) || 0)) {
        this.classList.remove("product-sticky-atc--hidden");
      } else {
        this.classList.add("product-sticky-atc--hidden");
      }
    });
  }
  connectedCallback() {
    this.productContext = this.closest("product-context") || void 0;
    if (!this.productContext) return;
    window.addEventListener("scroll", this.handleScroll);
    this.headerSection = document.querySelector(".shopify-section--header") || void 0;
  }
  disconnectedCallback() {
    window.removeEventListener("scroll", this.handleScroll);
  }
}
if (!customElements.get("product-sticky-atc")) {
  customElements.define("product-sticky-atc", ProductStickyATC);
}
